import React from "react";
import { toast } from "react-toastify";
import {
  Button,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  Form,
  TextInput,
} from "src/Components";
import { getUsers } from "src/Requests/connections";
import {
  getPortfolioGroupDropDown,
  previewUserAssign,
  setUserAssign,
  upsertGroupingSettingsUserAllowedCreditPercent,
} from "src/Requests/portfolioGroups";
import type {
  IUserAssign,
  IUserAssignPreview,
} from "src/Interfaces/portfolioGroups";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { getPercent } from "src/helper";
import AssignmentPreviewTables from "./AssignmentPreviewTables";

interface IProps {
  onSuccess: () => void;
  onClose: () => void;
}

const AssignmentForm: React.FC<IProps> = ({ onSuccess, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState<IUserAssignPreview | null>(null);
  const [formValues, setFormValues] = React.useState<
    Partial<IUserAssign> & { allowedCreditPercent?: string | null }
  >({});

  const onChange = (name: string, value: string | number | string[] | null) => {
    setPreview(null);
    setFormValues((prev) => ({
      ...prev,
      [name]: Array.isArray(value) ? value.map(Number) : value,
    }));
  };

  const getValidValues = () => {
    const portfoGroupId = Number(formValues.portfoGroupId);
    if (Number.isNaN(portfoGroupId)) {
      toast.error("گروه پرتفو را انتخاب کنید");
      return null;
    }

    if (!formValues.sahraUserIds?.length) {
      toast.error("اکانت را انتخاب کنید");
      return null;
    }

    const hasAllowedCreditPercent =
      formValues.allowedCreditPercent !== undefined &&
      formValues.allowedCreditPercent !== null &&
      formValues.allowedCreditPercent !== "";
    const allowedCreditPercent = hasAllowedCreditPercent
      ? getPercent(formValues.allowedCreditPercent ?? "")
      : null;

    if (hasAllowedCreditPercent && allowedCreditPercent == null) {
      toast.error("درصد اعتبار مجاز را وارد کنید");
      return null;
    }

    return {
      portfoGroupId,
      sahraUserIds: formValues.sahraUserIds,
      allowedCreditPercent,
    };
  };

  const previewHandler = () => {
    const validValues = getValidValues();
    if (!validValues) return;

    setLoading(true);
    previewUserAssign({
      sahraUserIds: validValues.sahraUserIds,
      portfoGroupId: validValues.portfoGroupId,
    })
      .then((resp) => {
        if (resp.data) setPreview(resp.data);
        else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const submitHandler = () => {
    const validValues = getValidValues();
    if (!validValues) return;

    setLoading(true);
    setUserAssign({
      sahraUserIds: validValues.sahraUserIds,
      portfoGroupId: validValues.portfoGroupId,
    })
      .then(async (resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        if (validValues.allowedCreditPercent != null) {
          const creditResponses = await Promise.all(
            validValues.sahraUserIds.map((sahraUserId) =>
              upsertGroupingSettingsUserAllowedCreditPercent({
                sahraUserId,
                value: Number(validValues.allowedCreditPercent),
              }),
            ),
          );
          const failedResponse = creditResponses.find((item) => !item.data);
          if (failedResponse) {
            toast.error(failedResponse.msg);
            return;
          }
        }

        toast.success(resp.msg || "با موفقیت ثبت شد");
        setFormValues({});
        onSuccess();
        onClose();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <Form
      className={`border border-gray-300 rounded p-4 gap-4 shrink-0 ${
        preview ? "!w-[560px]" : "!w-72"
      }`}
    >
      <div className="font-bold text-sm">تخصیص اکانت</div>
      {!preview ? (
        <>
          <DynamicMultiSelectDropdown
            dropdown={{
              name: "sahraUserIds",
              placeholder: "اکانت",
              extraLabel: true,
            }}
            getData={getUsers}
            onChange={onChange}
            value={formValues.sahraUserIds}
            withTagBox
          />
          <DynamicDropdown
            dropdown={{
              name: "portfoGroupId",
              placeholder: "گروه پرتفو",
              extraLabel: true,
            }}
            getData={getPortfolioGroupDropDown}
            onChange={onChange}
            value={formValues.portfoGroupId?.toString() ?? ""}
          />
          <TextInput
            label="درصد اعتبار مجاز"
            name="allowedCreditPercent"
            value={formValues.allowedCreditPercent ?? ""}
            onChange={onChange}
            kind="percent"
            extraLabel
          />
          <Button
            text="پیش نمایش"
            variant="contained"
            onClick={previewHandler}
            loading={loading}
          />
        </>
      ) : (
        <>
          <AssignmentPreviewTables preview={preview} />
          <div className="flex flex-row justify-end items-center gap-3">
            <Button
              text="بازگشت"
              variant="outlined"
              onClick={() => setPreview(null)}
              disabled={loading}
            />
            <Button
              text="ثبت"
              variant="contained"
              onClick={submitHandler}
              loading={loading}
            />
          </div>
        </>
      )}
    </Form>
  );
};

export default AssignmentForm;
